import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownItem, DropdownMenu, Icon } from "semantic-ui-react";

import { Dropdown, InterventionModal, useCan } from "components";
import { useShowPriceInVat } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { useTyreTitleAndSeasons } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/hooks/useTyreTitleAndSeasons";
import { ConnectInterventionModal } from "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/TyreReplacementTableRow/components/TyreActions/components/ConnectInterventionModal";
import { useTyreReplacementDeleteMutation } from "modules/AppointmentDetails/components/VehicleTyres/hooks";
import { useAppointmentDealer, useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { canWriteBackCustomerData, generateDMSNr } from "utils/appointmentUtils";
import { getDisplayedPrice } from "utils/common";

import type { Appointment, DealerLocation, QUESTION_RESULT_STATUSES, VehicleTyre, VehicleTyreReplacement } from "models";
import type { ConnectToInterventionPayload } from "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/TyreReplacementTableRow/components/TyreActions/components/ConnectInterventionModal";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";

type TyreActionSelectorProps = {
  vehicleTyre: VehicleTyre;
  replacement: VehicleTyreReplacement;
  appointment: Appointment;
  readOnly?: boolean;
};

export const vehicleTyreReplacementToIntervention = (
  title: string,
  replacement: VehicleTyreReplacement,
  description: string,
  status: QUESTION_RESULT_STATUSES | null,
  location: DealerLocation | undefined,
  showPriceInVat: boolean
): AddInterventionPayload => ({
  dms_nr: generateDMSNr(),
  title,
  replacements: [replacement],
  customer_ok: replacement.selected,
  description,
  price: getDisplayedPrice(replacement.price, location, showPriceInVat),
  appointment_id: replacement.appointment_id,
  question_result_status: status ?? undefined
});

export const TyreActionSelector = ({ vehicleTyre, replacement, appointment, readOnly }: TyreActionSelectorProps) => {
  const { t } = useTranslation();

  const [convertToInterventionPayload, setConvertToInterventionPayload] = useState<AddInterventionPayload | null>(null);
  const [connectInterventionPayload, setConnectInterventionPayload] = useState<ConnectToInterventionPayload | null>(null);

  const isConvertedOrConnected = replacement.intervention_id !== null;
  const isPositionConvertedOrConnected = vehicleTyre.replacements?.some(r => r.intervention_id);
  const isAnsweredByCustomer = appointment.customer_communication?.events?.some(event =>
    event.replacement_results?.some(rr => rr.vehicle_tyre_replacement_id === replacement.id)
  );

  const appointmentLocation = useAppointmentLocation(appointment);
  const appointmentDealer = useAppointmentDealer(appointment);
  const showPriceInVat = useShowPriceInVat();
  const canConnectToIntervention = useCan("update", "vehicle-tyre-replacements");
  const canCreateIntervention = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.Interventions);
  const { tyreTitle } = useTyreTitleAndSeasons(replacement.position);
  const tyreReplacementDeleteMutation = useTyreReplacementDeleteMutation();

  const handleConnectToIntervention = () => setConnectInterventionPayload({ title: tyreTitle, replacement: replacement });
  const handleConvertToIntervention = () =>
    setConvertToInterventionPayload(
      vehicleTyreReplacementToIntervention(
        tyreTitle,
        replacement,
        vehicleTyre.question_result_remark,
        vehicleTyre.question_result_status,
        appointmentLocation,
        showPriceInVat
      )
    );

  return (
    <div className="TyreReplacementActionSelector">
      <Dropdown
        disabled={readOnly || isConvertedOrConnected}
        className="action-dropdown"
        floating
        icon={null}
        direction="left"
        trigger={<Icon className={isConvertedOrConnected ? "link horizontal grey" : "plus grey"} />}
      >
        <DropdownMenu className="dropdown-menu">
          {!isPositionConvertedOrConnected && canCreateIntervention && (
            <DropdownItem onClick={handleConvertToIntervention}>
              <Icon className="screwdriver wrench" />
              {t("v8_convert_to_intervention", "Convert to Intervention")}
            </DropdownItem>
          )}

          {!isPositionConvertedOrConnected && canConnectToIntervention && (
            <DropdownItem onClick={handleConnectToIntervention}>
              <Icon className="link horizontal" />
              {t("v8_connect_to_intervention", "Connect to Intervention")}
            </DropdownItem>
          )}

          {!replacement.order_placed && !isAnsweredByCustomer && (
            <DropdownItem onClick={() => tyreReplacementDeleteMutation.mutate(replacement.id)}>
              <Icon className="trash grey" />
              {t("v8_remove", "Remove")}
            </DropdownItem>
          )}
        </DropdownMenu>
      </Dropdown>

      <InterventionModal
        appointmentId={appointment.id}
        carId={appointment.car_id}
        location={appointmentLocation}
        dealer={appointmentDealer}
        open={convertToInterventionPayload !== null}
        intervention={convertToInterventionPayload}
        onClose={() => setConvertToInterventionPayload(null)}
        canWriteBackCustomerData={canWriteBackCustomerData(appointmentLocation)}
      />

      <ConnectInterventionModal
        connectToInterventionPayload={connectInterventionPayload}
        open={connectInterventionPayload !== null}
        onClose={() => setConnectInterventionPayload(null)}
        appointment={appointment}
      />
    </div>
  );
};
